import type { TourMedia } from '../types';

/**
 * Release notes — the source of truth for the "What's new" banner, DECOUPLED
 * from the global tour (`steps/tour-steps.ts`).
 *
 * Why this exists: the What's-new banner used to read the tour-steps array and
 * filter by `addedInVersion`, which forced every "new feature" announcement to
 * also be a fully-anchored global-tour step. Splitting the announcement content
 * into its own registry lets a release note exist independently of the tour:
 *
 *   - release-only : a note with no `tourStepId` -> shown as a centered card in
 *                    the What's-new walkthrough (no DOM anchor required).
 *   - tour-only    : a tour step that ships without a matching note -> appears
 *                    in the full tour for new users but is NOT announced to
 *                    returning users.
 *   - both         : a note whose `tourStepId` points at a tour step -> the
 *                    walkthrough deep-links into that anchored step.
 *
 * Versioning stays single and monotonic: `ReleaseNote.version` shares the same
 * scale as `TourStep.addedInVersion` and the user's `onboardingSeenVersion`.
 * Bump `CURRENT_TOUR_VERSION` (`steps/tour-steps.ts`) whenever you add a note
 * with a higher version so returning users become eligible for the banner. The
 * `release-notes.test.ts` invariant guards that every `note.version` is within
 * `CURRENT_TOUR_VERSION` and every `tourStepId` resolves to a real step.
 */
export type ReleaseNote = {
  /** Stable id. The synthesized (anchorless) walkthrough step uses `rn-${id}`. */
  id: string;
  /** Product version this feature shipped in (same scale as `TourStep.addedInVersion`). */
  version: number;
  /** Headline shown in the banner copy and as the walkthrough card title. */
  title: string;
  /** One- or two-sentence body for the walkthrough card / release-only entry. */
  summary: string;
  /** Optional media for the card (image/video). Defaults to none when omitted. */
  media?: TourMedia;
  /**
   * Optional deep-link into a global tour step (`TourStep.id`). When set and the
   * step is visible to the user, the What's-new walkthrough reuses that anchored
   * step; otherwise the note renders as a centered, anchorless card.
   */
  tourStepId?: string;
};

/**
 * Release notes, oldest-first. The What's-new walkthrough plays them in array
 * order, so keep related entries grouped and ordered the way a returning user
 * should be guided through them.
 */
export const getReleaseNotes = (): ReleaseNote[] => [
  {
    id: 'trades-rework',
    version: 2,
    title: 'Trades, reworked',
    summary:
      'The Trades page now scopes your book per counterparty with auto-generated view tabs, splits Stock Lend/Borrow and Repo exposure in a metrics bar, and surfaces per-position lifecycle actions in a contextual action bar.',
    tourStepId: 'trades-overview',
  },
  {
    id: 'single-trade',
    version: 2,
    title: 'Single trade view',
    summary:
      'Open any position to its own page: vital-sign KPIs (P&L per day, rate, exposure, mark-vs-booking, adequacy) up top, with Analytics, Activity and Trade Terms tabs below.',
    tourStepId: 'single-trade-detail',
  },
  {
    id: 'mark-to-market',
    version: 2,
    title: 'Mark to Market',
    summary:
      'A dedicated Mark-to-Market workspace: pick a counterparty, review Stock L/B and Repo bucket totals, enter new prices across the grid, and run the bulk mark in one go.',
    tourStepId: 'mark-to-market',
  },
  {
    id: 'history',
    version: 2,
    title: 'History',
    summary:
      'A History mode with two logs: the terminal Trades blotter, and a Mark-to-Market run log that drills day -> counterparty -> run down to per-instrument price moves.',
    tourStepId: 'trade-history',
  },
  // LR-474: settlement currency becomes the primary display currency.
  {
    id: 'settlement-ccy-primary',
    version: 3,
    title: 'Settlement currency first',
    summary:
      'Money columns now lead with the settlement currency you chose for the trade — the security-currency original sits in a hover tooltip. Per-currency totals, the summary-bar currency filter and CSV subtotals all group by settlement currency, and a new Settlement Ccy column joins the blotter.',
  },
  // LR-617: fee entry mode is GMSLA-only; repos always book as Rebate.
  {
    id: 'repo-rebate-only',
    version: 4,
    title: 'Repo trades book as Rebate',
    summary:
      'Rate Entry Mode no longer offers Fee on Repo and Reverse Repo trades — GMRA economics are a repo rate, so those trades always book as Rebate. Switching a trade to Repo now moves the rate mode back to Rebate for you. Fee mode is unchanged for stock lend and borrow.',
  },
  // LR-686: the Require Attention section is gone from the table; the amber
  // Attention chip in the metrics bar carries the count and the one-click filter.
  // Release-only (no `tourStepId`): the chip lives inside the metrics-bar frame of
  // `trades-overview`, and notes deep-link to steps, not frames — pointing at the
  // step would replay the whole Trades walkthrough to say one thing.
  {
    id: 'trades-attention-chip',
    version: 5,
    title: 'One flat trade book',
    summary:
      'The Trades table is a single flat list again: sorting a column now orders the whole book instead of two stacked sections. Trades that need you — pending and rejected — keep an amber accent on the row, and the new Attention chip in the metrics bar counts them for the current view and filters the book down to them in one click.',
  },
  // LR-695: Terminate fixes the day the loan ends instead of flipping the status
  // on Confirm, and Set Close Date waits for an end date. Release-only for the
  // same reason as LR-686: both actions live inside the action-bar frame of
  // `trades-overview`, and a note deep-links to a step, not a frame.
  {
    id: 'terminate-fixes-the-end',
    version: 6,
    title: 'Terminate fixes the end date',
    summary:
      'Terminate now names the day the loan ends: that date becomes the Maturity Date and the close date at once, and the position stays Active — accruing — until then, when it matures and closes. Terminating for today still ends the trade immediately. Set Close Date appears only once a trade has an end date — from booking, from Give Notice or from Terminate — so a close date can no longer sit on an open trade doing nothing.',
  },
  // LR-844: Repo / Reverse Repo take Cash collateral only.
  {
    id: 'repo-cash-collateral-only',
    version: 7,
    title: 'Repo trades take Cash collateral',
    summary:
      'Collateral Type no longer offers Non-cash or Uncollateralized on Repo and Reverse Repo trades — a repo is a sale against cash, so those trades always book with Cash collateral. Switching a trade to Repo now moves the collateral back to Cash for you. Stock lend and borrow keep all three types.',
  },
];

/** Release notes newer than the user's last-seen onboarding version. */
export const releaseNotesSince = (seenVersion: number): ReleaseNote[] =>
  getReleaseNotes().filter((note) => note.version > seenVersion);
